import {
  CarrierDeliveryMethod,
  OwnDeliveryMethod,
  PickupDeliveryMethod,
  PickupDeliveryRule,
  ProviderWarehouseDelivery,
  RunsDeliveryMethod
} from 'src/typings';
import { kyivWallTime } from 'src/app/shared/utils/kyiv-date-time.util';

// Дни недели — ISO: 1 = Пн ... 7 = Вс.
const DAY_SHORT = ['', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const DAY_IN = [
  '',
  'в понедельник',
  'во вторник',
  'в среду',
  'в четверг',
  'в пятницу',
  'в субботу',
  'в воскресенье'
];

const LOOKAHEAD_DAYS = 14;

function isoWeekday(date: Date): number {
  return date.getUTCDay() === 0 ? 7 : date.getUTCDay();
}

function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function startOfDay(date: Date): Date {
  const res = new Date(date);
  res.setUTCHours(0, 0, 0, 0);
  return res;
}

function addDays(date: Date, days: number): Date {
  const res = new Date(date);
  res.setUTCDate(res.getUTCDate() + days);
  return res;
}

function atTime(day: Date, time: string): Date {
  const res = startOfDay(day);
  const [hours, minutes] = time.split(':').map(Number);
  res.setUTCHours(hours, minutes, 0, 0);
  return res;
}

function diffInDays(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / 86400000);
}

/** «сегодня» / «завтра» / «в пятницу» / «09.07» относительно now. */
export function relativeDayLabel(now: Date, target: Date): string {
  return relativeCalendarDayLabel(kyivWallTime(now), kyivWallTime(target));
}

function relativeCalendarDayLabel(now: Date, target: Date): string {
  const diff = diffInDays(now, target);
  if (diff <= 0) return 'сегодня';
  if (diff === 1) return 'завтра';
  if (diff <= 6) return DAY_IN[isoWeekday(target)];
  const dd = `${target.getUTCDate()}`.padStart(2, '0');
  const mm = `${target.getUTCMonth() + 1}`.padStart(2, '0');
  return `${dd}.${mm}`;
}

/** [1,2,3,4,5] → «Пн–Пт», [1..7] → «ежедневно», [1,3,5] → «Пн, Ср, Пт». */
export function formatDays(days: number[] | undefined): string {
  const list = [...new Set(days || [])].filter((d) => d >= 1 && d <= 7).sort();
  if (!list.length) return '';
  if (list.length === 7) return 'ежедневно';

  const isSequential = list.every((day, index) => index === 0 || day === list[index - 1] + 1);
  if (isSequential && list.length > 2) {
    return `${DAY_SHORT[list[0]]}–${DAY_SHORT[list[list.length - 1]]}`;
  }
  return list.map((day) => DAY_SHORT[day]).join(', ');
}

function offsetLabel(offset: number): string {
  if (offset === 0) return 'в тот же день';
  if (offset === 1) return 'на след. день';
  return `через ${offset} дн.`;
}

function arrivalWindowLabel(from?: string, to?: string): string {
  if (from && to) return `${from}–${to}`;
  if (from) return `к ${from}`;
  if (to) return `до ${to}`;
  return '';
}

/** Правила самовывоза с поддержкой легаси-формата (плоские days/from/to). */
export function getPickupRules(method: PickupDeliveryMethod): PickupDeliveryRule[] {
  if (method.rules?.length) return method.rules;
  if (method.days?.length || method.from || method.to) {
    return [{ days: method.days || [], from: method.from, to: method.to }];
  }
  return [];
}

/** «Пн–Пт 09:00–17:00; Сб 09:00–15:00» или '' если расписания нет. */
function formatPickupSchedule(method: PickupDeliveryMethod): string {
  return getPickupRules(method)
    .map((rule) => {
      const days = formatDays(rule.days);
      const hours = rule.from || rule.to ? `${rule.from || ''}–${rule.to || ''}` : '';
      return [days, hours].filter(Boolean).join(' ');
    })
    .filter(Boolean)
    .join('; ');
}

/** Статичные строки-описания правил доставки (для таблицы складов и профиля). */
export function summarizeDelivery(
  delivery: ProviderWarehouseDelivery | null | undefined
): string[] {
  if (!delivery) return [];
  const lines: string[] = [];

  (delivery.methods || []).forEach((method) => {
    switch (method.type) {
      case 'own': {
        const rules = method.rules
          .map((rule) => {
            const window = arrivalWindowLabel(rule.arrivalFrom, rule.arrivalTo);
            return `${formatDays(rule.days)} до ${rule.cutoff} → ${offsetLabel(
              rule.arrivalDayOffset
            )}${window ? ' ' + window : ''}${rule.arrivalDays?.length ? ' · дни доставки: ' + formatDays(rule.arrivalDays) : ''}`;
          })
          .join('; ');
        lines.push(`Доставка: ${rules}`);
        break;
      }
      case 'carrier': {
        const rules = method.rules
          .map((rule) => {
            const shipLabel = `отправка ${offsetLabel(rule.shipDayOffset || 0)}`;
            const transit = rule.transitDays ? `, в пути ~${rule.transitDays} дн.` : '';
            return `${formatDays(rule.days)} до ${rule.cutoff} — ${shipLabel}${transit}`;
          })
          .join('; ');
        lines.push(`Перевозчик${method.carrier ? ' (' + method.carrier + ')' : ''}: ${rules}`);
        break;
      }
      case 'runs': {
        const rules = method.rules
          .map((rule) => {
            const transit = rule.transitMinutes ? ` (~${rule.transitMinutes} мин)` : '';
            return `${formatDays(rule.days)}${rule.cutoff ? ' · заказ до ' + rule.cutoff : ''} · выезд ${rule.departures.join(', ')}${rule.arrivalFrom || rule.arrivalTo ? ' → ' + offsetLabel(rule.arrivalDayOffset || 0) + ' ' + arrivalWindowLabel(rule.arrivalFrom, rule.arrivalTo) : transit}`;
          })
          .join('; ');
        lines.push(`Рейсы: ${rules}`);
        break;
      }
      case 'pickup': {
        const schedule = formatPickupSchedule(method);
        const travel = method.travelMinutes != null ? ` · ${method.travelMinutes} мин в одну сторону` : ' · время дороги не указано';
        const handling = method.handlingMinutes != null ? ` · выдача ${method.handlingMinutes} мин` : '';
        lines.push(`Самовывоз${schedule ? ': ' + schedule : ''}${travel}${handling}`);
        break;
      }
    }
  });

  if (delivery.note) lines.push(delivery.note);
  return lines;
}

interface DeliveryCandidate {
  arrival: Date;
  label: string;
}

function ownCandidates(method: OwnDeliveryMethod, now: Date): DeliveryCandidate[] {
  const candidates: DeliveryCandidate[] = [];
  const nowMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();

  for (let dayIdx = 0; dayIdx <= LOOKAHEAD_DAYS; dayIdx++) {
    const orderDay = addDays(now, dayIdx);
    const weekday = isoWeekday(orderDay);

    method.rules.forEach((rule) => {
      if (!rule.days.includes(weekday)) return;
      if (dayIdx === 0 && nowMinutes > timeToMinutes(rule.cutoff)) return;

      let arrivalDay = addDays(orderDay, rule.arrivalDayOffset);
      if (rule.arrivalDays?.length) {
        for (let skipped = 0; skipped < 7 && !rule.arrivalDays.includes(isoWeekday(arrivalDay)); skipped++) arrivalDay = addDays(arrivalDay, 1);
      }
      const arrival = atTime(arrivalDay, rule.arrivalTo || rule.arrivalFrom || '23:59');
      if (arrival < now) return;
      const window = arrivalWindowLabel(rule.arrivalFrom, rule.arrivalTo);
      const orderPart =
        dayIdx === 0
          ? `заказ до ${rule.cutoff}`
          : `заказ ${relativeCalendarDayLabel(now, orderDay)} до ${rule.cutoff}`;
      candidates.push({
        arrival,
        label: `${relativeCalendarDayLabel(now, arrivalDay)}${window ? ' ' + window : ''} (${orderPart})`
      });
    });
  }

  return candidates;
}

function runsCandidates(method: RunsDeliveryMethod, now: Date): DeliveryCandidate[] {
  const candidates: DeliveryCandidate[] = [];
  for (let dayIdx = 0; dayIdx <= LOOKAHEAD_DAYS; dayIdx++) {
    const runDay = addDays(now, dayIdx);
    for (const rule of method.rules || []) {
      if (!rule.days?.includes(isoWeekday(runDay))) continue;
      for (const departure of rule.departures || []) {
        const leave = atTime(runDay, departure);
        const deadline = atTime(runDay, rule.cutoff || departure);
        if (deadline < now || leave <= now || deadline > leave) continue;
        const arrivalDay = addDays(runDay, rule.arrivalDayOffset || 0);
        const arrival = rule.arrivalFrom || rule.arrivalTo
          ? atTime(arrivalDay, rule.arrivalTo || rule.arrivalFrom!)
          : rule.transitMinutes != null && rule.transitMinutes > 0
            ? new Date(leave.getTime() + rule.transitMinutes * 60000) : null;
        // Неизвестное время в пути не означает мгновенную доставку.
        if (!arrival || arrival < leave) continue;
        const time = `${arrival.getUTCHours()}`.padStart(2, '0') + ':' + `${arrival.getUTCMinutes()}`.padStart(2, '0');
        const window = arrivalWindowLabel(rule.arrivalFrom, rule.arrivalTo);
        candidates.push({ arrival, label: window
          ? `рейс ${relativeCalendarDayLabel(now, arrivalDay)} ${window} (заказ до ${rule.cutoff || departure})`
          : `рейс ${relativeCalendarDayLabel(now, runDay)} ${departure} → ~${time}` });
      }
    }
  }
  return candidates;
}

function pickupCandidates(method: PickupDeliveryMethod, now: Date): DeliveryCandidate[] {
  // Оба значения должны быть явно настроены. Ноль допустим, отсутствие — неизвестный срок.
  if (method.travelMinutes == null || method.handlingMinutes == null) return [];
  const travel = Number(method.travelMinutes), handling = Number(method.handlingMinutes);
  if (!Number.isFinite(travel) || !Number.isFinite(handling) || travel < 0 || handling < 0) return [];
  const rules = method.rules?.length ? method.rules : [{ days: method.days || [], from: method.from, to: method.to }];
  const candidates: DeliveryCandidate[] = [];
  for (let dayIdx = 0; dayIdx <= LOOKAHEAD_DAYS; dayIdx++) {
    const day = addDays(now, dayIdx);
    for (const rule of rules) {
      if ((rule.days?.length && !rule.days.includes(isoWeekday(day))) || !rule.from || !rule.to || rule.to <= rule.from) continue;
      const reached = new Date(Math.max(now.getTime() + travel * 60000, atTime(day, rule.from).getTime()));
      const issued = new Date(reached.getTime() + handling * 60000);
      if (issued > atTime(day, rule.to)) continue;
      const arrival = new Date(issued.getTime() + travel * 60000);
      const time = `${arrival.getUTCHours()}`.padStart(2, '0') + ':' + `${arrival.getUTCMinutes()}`.padStart(2, '0');
      candidates.push({ arrival, label: `самовывоз · ${travel} мин в одну сторону · у нас ${relativeCalendarDayLabel(now, arrival)} ~${time}` });
    }
  }
  return candidates;
}

function carrierCandidates(method: CarrierDeliveryMethod, now: Date): DeliveryCandidate[] {
  const candidates: DeliveryCandidate[] = [];
  const nowMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  const name = method.carrier || 'перевозчик';

  for (let dayIdx = 0; dayIdx <= LOOKAHEAD_DAYS; dayIdx++) {
    const orderDay = addDays(now, dayIdx);
    const weekday = isoWeekday(orderDay);

    method.rules.forEach((rule) => {
      if (!rule.days.includes(weekday)) return;
      if (dayIdx === 0 && nowMinutes > timeToMinutes(rule.cutoff)) return;

      const shipDay = addDays(orderDay, rule.shipDayOffset || 0);
      const transitDays = rule.transitDays ?? 1;
      // Точного времени прибытия у перевозчика нет — берём полдень для ранжирования.
      const arrival = atTime(addDays(shipDay, transitDays), '12:00');
      const orderPart =
        dayIdx === 0 ? `до ${rule.cutoff}` : `${relativeCalendarDayLabel(now, orderDay)} до ${rule.cutoff}`;
      const transit = rule.transitDays ? `, в пути ~${rule.transitDays} дн.` : '';
      candidates.push({
        arrival,
        label: `${name}: отправка ${relativeCalendarDayLabel(now, shipDay)} (заказ ${orderPart})${transit}`
      });
    });
  }

  return candidates;
}

function pickupLabel(method: PickupDeliveryMethod): string {
  const schedule = formatPickupSchedule(method);
  return `самовывоз${schedule ? ' ' + schedule : ''}`;
}

/**
 * Ближайший вариант получения товара с учётом текущего времени и дня недели.
 * Возвращает компактную строку для тултипов, например:
 * «завтра 10:00–11:00 (заказ до 17:00)» или «рейс сегодня 14:00 → ~15:00».
 */
export function deliveryEtaLine(
  delivery: ProviderWarehouseDelivery | null | undefined,
  now: Date = new Date()
): string {
  if (!delivery?.methods?.length) return '';
  now = kyivWallTime(now);
  if (!Number.isFinite(now.getTime())) return '';

  const candidates: DeliveryCandidate[] = [];
  let pickup: PickupDeliveryMethod | undefined;

  delivery.methods.forEach((method) => {
    switch (method.type) {
      case 'own':
        candidates.push(...ownCandidates(method, now));
        break;
      case 'runs':
        candidates.push(...runsCandidates(method, now));
        break;
      case 'carrier':
        candidates.push(...carrierCandidates(method, now));
        break;
      case 'pickup':
        pickup = method;
        candidates.push(...pickupCandidates(method, now));
        break;
    }
  });

  if (!candidates.length) return pickup ? pickupLabel(pickup) : '';

  candidates.sort((left, right) => left.arrival.getTime() - right.arrival.getTime());
  return candidates[0].label;
}
